import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import type { RowSelectionState } from '@tanstack/react-table';
import { DataTable } from '~/components/ui/data-table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import type { AppLayoutContext } from '~/types';
import {
  decodeTableFilters,
  encodeTableFilters,
  getFacetColumnIds,
  hasTableFiltersInUrl,
  type ColumnFilter,
} from '~/utils/tableFilters';
import { useVoidInvoice } from '~/pages/FamilyDetail/useVoidInvoice';
import { useDeleteInvoice } from '~/pages/FamilyDetail/useDeleteInvoice';
import { useSchoolInvoicesQuery } from './useSchoolInvoicesQuery';
import { getSchoolInvoiceColumns } from './columns';
import { selectable, toTableRow, type SchoolInvoiceTableRow } from './invoiceRules';
import { PostSchoolInvoicesDialog, type PostSchoolInvoicesOutcome } from './PostSchoolInvoicesDialog';
import { EditSchoolDraftsDialog } from './EditSchoolDraftsDialog';
import { DeleteSchoolDraftsDialog } from './DeleteSchoolDraftsDialog';
import type { UpdateSchoolDraftsPayload } from './useUpdateSchoolDrafts';
import type { DeleteSchoolDraftsPayload } from './useDeleteSchoolDrafts';

const FILTER_PREFIX = 'invoice';
const SEARCH_COLUMN_ID = 'family';
const STATUS_COLUMN_ID = 'displayStatus';
const CLOSED_STATUSES = ['Paid', 'Void'];

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

type DraftsPayload = UpdateSchoolDraftsPayload | DeleteSchoolDraftsPayload;

// What a bulk edit or delete has to say beyond its count: each family the
// backend refused, and each selected row it left alone because it was not a
// draft, named by memo and family so the admin can find it in the table.
function draftsProblems(payload: DraftsPayload, selected: readonly SchoolInvoiceTableRow[]) {
  const byId = new Map(selected.map((row) => [row.invoiceId, row]));
  const problems = payload.userErrors?.map((issue) => issue.message) ?? [];
  const skipped: string[] = [];
  for (const family of payload.families) {
    for (const row of family.rows) {
      if (row.action !== 'skipped') continue;
      const selectedRow = byId.get(row.invoiceId);
      skipped.push(
        `Skipped ${selectedRow?.memo ?? 'invoice'} for ${family.familyName}: ${row.message ?? 'not a draft'}`
      );
    }
  }
  return { problems, skipped };
}

function SchoolInvoices() {
  const { roleContext } = useOutletContext<AppLayoutContext>();
  const { schoolSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const schoolYear = roleContext?.selectedSchoolYear;
  const schoolYearId = schoolYear?.id;

  const { data, loading, error, refetch } = useSchoolInvoicesQuery(schoolYearId);
  const [voidInvoice, { loading: voiding }] = useVoidInvoice();
  const [deleteInvoice, { loading: deleting }] = useDeleteInvoice();

  const [notice, setNotice] = useState<ReactNode | null>(null);
  const [noticeTone, setNoticeTone] = useState<'success' | 'error'>('success');
  const showNotice = useCallback((message: ReactNode, tone: 'success' | 'error' = 'success') => {
    setNoticeTone(tone);
    setNotice(message);
  }, []);

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [postingRows, setPostingRows] = useState<SchoolInvoiceTableRow[] | null>(null);
  const [editingRows, setEditingRows] = useState<SchoolInvoiceTableRow[] | null>(null);
  const [deletingRows, setDeletingRows] = useState<SchoolInvoiceTableRow[] | null>(null);
  const [invoiceToVoid, setInvoiceToVoid] = useState<SchoolInvoiceTableRow | null>(null);
  const [invoiceToDelete, setInvoiceToDelete] = useState<SchoolInvoiceTableRow | null>(null);

  const rows = useMemo(() => (data?.schoolInvoices ?? []).map(toTableRow), [data?.schoolInvoices]);
  const selectedCount = useMemo(
    () => rows.reduce((count, row) => count + (rowSelection[row.invoiceId] ? 1 : 0), 0),
    [rows, rowSelection]
  );
  const selectedDraftCount = useMemo(
    () => rows.reduce((count, row) => count + (rowSelection[row.invoiceId] && row.status === 'DRAFT' ? 1 : 0), 0),
    [rows, rowSelection]
  );
  const noDraftSelected = selectedDraftCount === 0;

  const columns = useMemo(
    () => getSchoolInvoiceColumns({ schoolSlug, onVoid: setInvoiceToVoid, onDelete: setInvoiceToDelete }),
    [schoolSlug]
  );

  // Filters live in the URL, as on Enrollments, so a filtered list survives a
  // refresh and can be handed to someone.
  const facetColumnIds = useMemo(() => getFacetColumnIds(columns), [columns]);
  const filtersFromUrl = useMemo(
    () =>
      decodeTableFilters({
        searchParams,
        prefix: FILTER_PREFIX,
        facetColumnIds,
        searchColumnId: SEARCH_COLUMN_ID,
      }),
    [searchParams, facetColumnIds]
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>(filtersFromUrl);

  useEffect(() => setColumnFilters(filtersFromUrl), [filtersFromUrl]);

  useEffect(() => {
    const next = encodeTableFilters({
      searchParams,
      prefix: FILTER_PREFIX,
      facetColumnIds,
      searchColumnId: SEARCH_COLUMN_ID,
      filters: columnFilters,
    });
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters, facetColumnIds]);

  // First visit, nothing in the URL: what still needs attention, which is every
  // status but Paid and Void. Facets are inclusive, so the default is the list
  // of open statuses the data actually has; Reset clears it and shows all.
  const defaultApplied = useRef(false);
  useEffect(() => {
    if (defaultApplied.current || !data) return;
    defaultApplied.current = true;
    if (hasTableFiltersInUrl({ searchParams, prefix: FILTER_PREFIX, facetColumnIds })) return;
    const openStatuses = Array.from(new Set(rows.map((row) => row.displayStatus))).filter(
      (status) => !CLOSED_STATUSES.includes(status)
    );
    if (openStatuses.length > 0) {
      setColumnFilters([{ id: STATUS_COLUMN_ID, value: openStatuses }]);
    }
  }, [data, rows, searchParams, facetColumnIds]);

  const handlePostCompleted = useCallback(
    ({ payload, failedInvoiceIds }: PostSchoolInvoicesOutcome) => {
      // Failed families stay selected so a retry is one click.
      setRowSelection(Object.fromEntries(failedInvoiceIds.map((id) => [id, true])));
      const posted = `Posted ${plural(payload.pushedCount, 'invoice', 'invoices')} across ${plural(
        payload.familyCount,
        'family',
        'families'
      )}.`;
      if (payload.success) {
        showNotice(posted);
      } else {
        const problems = payload.userErrors?.map((issue) => issue.message) ?? [];
        showNotice(
          <>
            {posted} {plural(problems.length, 'problem', 'problems')}:
            <ul className="mt-1 list-disc pl-5 font-normal">
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          </>,
          'error'
        );
      }
      void refetch();
    },
    [refetch, showNotice]
  );

  // A bulk edit or delete clears the selection: the rows it changed have a new
  // description or are gone, and a skipped row is not worth a retry.
  const showDraftsOutcome = useCallback(
    (headline: string, success: boolean, payload: DraftsPayload, selected: readonly SchoolInvoiceTableRow[]) => {
      setRowSelection({});
      const { problems, skipped } = draftsProblems(payload, selected);
      if (success && skipped.length === 0) {
        showNotice(headline);
      } else {
        showNotice(
          <>
            {headline}
            {problems.length > 0 && ` ${plural(problems.length, 'problem', 'problems')}:`}
            {(problems.length > 0 || skipped.length > 0) && (
              <ul className="mt-1 list-disc pl-5 font-normal">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
                {skipped.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
          </>,
          success ? 'success' : 'error'
        );
      }
      void refetch();
    },
    [refetch, showNotice]
  );

  const handleEditCompleted = useCallback(
    (payload: UpdateSchoolDraftsPayload) => {
      showDraftsOutcome(
        `Updated ${plural(payload.updatedCount, 'draft', 'drafts')} across ${plural(payload.familyCount, 'family', 'families')}.`,
        payload.success,
        payload,
        editingRows ?? []
      );
    },
    [editingRows, showDraftsOutcome]
  );

  const handleDeleteCompleted = useCallback(
    (payload: DeleteSchoolDraftsPayload) => {
      showDraftsOutcome(
        `Deleted ${plural(payload.deletedCount, 'draft', 'drafts')} across ${plural(payload.familyCount, 'family', 'families')}.`,
        payload.success,
        payload,
        deletingRows ?? []
      );
    },
    [deletingRows, showDraftsOutcome]
  );

  const confirmDelete = useCallback(async () => {
    if (!invoiceToDelete) return;
    const result = await deleteInvoice({ variables: { input: { invoiceId: invoiceToDelete.invoiceId } } });
    const failure = result.data?.deleteInvoice?.userErrors?.[0]?.message;
    if (failure) {
      showNotice(failure, 'error');
    } else {
      showNotice(`Deleted ${invoiceToDelete.memo ?? 'the draft'} for ${invoiceToDelete.familyName}.`);
      setRowSelection((selection) => {
        if (!selection[invoiceToDelete.invoiceId]) return selection;
        const next = { ...selection };
        delete next[invoiceToDelete.invoiceId];
        return next;
      });
      void refetch();
    }
    setInvoiceToDelete(null);
  }, [invoiceToDelete, deleteInvoice, refetch, showNotice]);

  const confirmVoid = useCallback(async () => {
    if (!invoiceToVoid) return;
    const result = await voidInvoice({ variables: { input: { invoiceId: invoiceToVoid.invoiceId } } });
    const failure = result.data?.voidInvoice?.userErrors?.[0]?.message;
    if (failure) {
      showNotice(failure, 'error');
    } else {
      showNotice(`Voided ${invoiceToVoid.memo ?? 'the invoice'} for ${invoiceToVoid.familyName}.`);
      void refetch();
    }
    setInvoiceToVoid(null);
  }, [invoiceToVoid, voidInvoice, refetch, showNotice]);

  if (!schoolYearId) {
    return (
      <div className="p-6">
        <div className="text-center text-muted-foreground">Please select a school year to view invoices.</div>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 rounded bg-muted"></div>
          <div className="h-64 rounded bg-muted"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-destructive">Error loading invoices: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Invoices</h1>
        <p className="text-muted-foreground">
          {schoolYear?.name} · {plural(rows.length, 'invoice', 'invoices')}
        </p>
      </div>

      {notice && (
        <div
          role="status"
          className={`mb-4 flex items-start justify-between gap-4 rounded-md border p-4 ${
            noticeTone === 'error' ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700'
          }`}
        >
          <div className="font-medium">{notice}</div>
          <button type="button" className="text-sm underline" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </div>
      )}

      <DataTable
        columns={columns}
        data={rows}
        searchColumn={SEARCH_COLUMN_ID}
        searchPlaceholder="Filter by family or description..."
        defaultSorting={[{ id: 'dueDate', desc: false }]}
        stateKey={`school-invoices-${schoolYearId}`}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        enableRowSelection={(row) => selectable(row.original)}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        getRowId={(row) => row.invoiceId}
        bulkActions={[
          {
            label: `Post ${plural(selectedCount, 'invoice', 'invoices')}`,
            variant: 'default',
            onClick: (selected) => setPostingRows(selected),
          },
          {
            label: 'Edit drafts',
            variant: 'outline',
            disabled: noDraftSelected,
            title: noDraftSelected ? 'Select at least one draft' : undefined,
            onClick: (selected) => setEditingRows(selected),
          },
          {
            label: 'Delete drafts',
            variant: 'destructive',
            disabled: noDraftSelected,
            title: noDraftSelected ? 'Select at least one draft' : undefined,
            onClick: (selected) => setDeletingRows(selected),
          },
        ]}
      />

      {editingRows && (
        <EditSchoolDraftsDialog
          open
          onOpenChange={(open) => { if (!open) setEditingRows(null); }}
          schoolYearId={schoolYearId}
          invoices={editingRows}
          onCompleted={handleEditCompleted}
        />
      )}

      {deletingRows && (
        <DeleteSchoolDraftsDialog
          open
          onOpenChange={(open) => { if (!open) setDeletingRows(null); }}
          schoolYearId={schoolYearId}
          invoices={deletingRows}
          onCompleted={handleDeleteCompleted}
        />
      )}

      <AlertDialog open={!!invoiceToDelete} onOpenChange={(open) => { if (!open) setInvoiceToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete draft</AlertDialogTitle>
            <AlertDialogDescription>
              Delete {invoiceToDelete?.memo ?? 'this draft'} for {invoiceToDelete?.familyName}? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void confirmDelete();
              }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Delete draft'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {postingRows && (
        <PostSchoolInvoicesDialog
          open
          onOpenChange={(open) => { if (!open) setPostingRows(null); }}
          schoolYearId={schoolYearId}
          invoices={postingRows}
          onCompleted={handlePostCompleted}
        />
      )}

      <AlertDialog open={!!invoiceToVoid} onOpenChange={(open) => { if (!open) setInvoiceToVoid(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Void invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Void {invoiceToVoid?.memo ?? 'this invoice'} for {invoiceToVoid?.familyName}? The family will no longer
              be able to pay it. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void confirmVoid();
              }}
              disabled={voiding}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {voiding ? 'Voiding...' : 'Void invoice'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default SchoolInvoices;
